import { KeyboardArrowLeft as KeyboardArrowLeftIcon } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { isArray, isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { CustomButton } from 'src/components/button/Button';
import { CardGrid, CardGridItem } from 'src/components/cardGrid';
import { FilterList } from 'src/components/filterList';
import { ActionBar, Header } from 'src/components/header';
import { Modal } from 'src/components/modal/Modal';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Progress } from 'src/components/progress/Progress';
import { PATHS } from 'src/constants/paths';
import { useModal } from 'src/hooks/useModal';
import type { IFilter, ILayout } from 'src/interfaces';
import { lang, mapEscapeRegExWithExclusions } from 'src/utils';

import { useSelectLayoutForm } from '../../hooks/SelectLayoutForm.hook';
import { AssemblyModal } from '../assemblyModal/AssemblyModal';
import { LayoutCard } from '../layoutCard';
import {
  type SelectLayoutFormProps,
  INoScreens,
  IOrientation,
} from './SelectLayoutForm.interface';
import {
  ButtonContainer,
  CardGridWrapper,
  Container,
  FixedWidthColumn,
  FluidWidthColumn,
  Wrapper,
} from './SelectLayoutForm.styled';

export const SelectLayoutForm = (props: SelectLayoutFormProps) => {
  const modal = useModal();

  const navigate = useNavigate();

  const {
    searchVal,
    selectedLayout,
    totalNumOfLayouts,
    layoutsDetails,
    isLayoutsLoading,
    errorLayouts,
    getNextLayoutPage,
    onSelectedLayout,
    onChangedRadioSearchParam,
    onChangedSearchVal,
  } = useSelectLayoutForm(props.layoutTypeId);

  const handleChange = (key: string, val: string | string[]) => {
    onSelectedLayout(undefined);

    if (key === 'Orientation') {
      onChangedRadioSearchParam((prevState) => ({
        ...prevState,
        orientation: val as string,
      }));
    } else if (key === 'Number of screens') {
      onChangedRadioSearchParam((prevState) => ({
        ...prevState,
        num_screens: Number(val),
      }));
    }
  };

  const onRemoveKeyword = (value: string) => {
    onSelectedLayout(undefined);

    const updatedData = searchVal.filter((item) => item !== value);
    onChangedSearchVal(updatedData);
  };

  const onChangeKeyword = (value: string | string[]) => {
    onSelectedLayout(undefined);

    if (isEmpty(value)) {
      return onChangedSearchVal([]);
    }

    const keywords = isArray(value) ? value : [value];
    const current_keyword = keywords.pop() || '';
    const escapedString = mapEscapeRegExWithExclusions(current_keyword, []);

    onChangedSearchVal([...keywords, escapedString as string]);
  };

  const onReset = () => {
    onSelectedLayout(undefined);

    onChangedSearchVal([]);
    onChangedRadioSearchParam({
      orientation: '',
      num_screens: 0,
    });
  };

  const handleCancel = () => {
    navigate({
      pathname: PATHS.ASSEMBLIES,
    });
  };

  const handleBack = () => {
    props.setActive('layoutType');
  };

  const handleNext = () => {
    modal.onOpenModal();
  };

  const handleClick = (layout: ILayout) => {
    onSelectedLayout(layout);
  };

  const handleClose = () => {
    modal.onCloseModal();
  };

  const filters: IFilter[] = [
    {
      label: 'Orientation',
      fieldType: 'radio',
      options: Object.values(IOrientation).map((value) => ({
        value: value.toLowerCase(),
        label: value,
      })),
    },
    {
      label: 'Number of screens',
      fieldType: 'radio',
      options: Object.values(INoScreens).map((value) => ({
        value: value,
        label: value,
      })),
    },
  ];

  if (errorLayouts) {
    // TODO: Replace with error component
    return <p>{lang.error} </p>;
  }

  return (
    <Container data-testid="layoutContainer">
      <Header
        title="Select layout"
        actionBar={
          <ActionBar numResults={totalNumOfLayouts} type={lang.selectLayout} />
        }
        breadcrumbItems={[
          {
            label: lang.assemblies,
            link: PATHS.ASSEMBLIES,
          },
          {
            label: lang.selectLayout,
          },
        ]}
      />

      <Wrapper data-testid="contentWrapper">
        <FixedWidthColumn>
          <FilterList
            filters={filters}
            onChangeFilter={handleChange}
            searchVal={searchVal}
            onRemoveKeyword={onRemoveKeyword}
            onChangeKeyword={onChangeKeyword}
            onReset={onReset}
            showReset
          />
        </FixedWidthColumn>

        <FluidWidthColumn>
          {isLayoutsLoading ? (
            <Progress />
          ) : (
            <>
              <NoResultAlert
                isLoading={isLayoutsLoading}
                dataToCheck={layoutsDetails.pages.flatMap(
                  (item) => item.layouts,
                )}
              />
              <CardGridWrapper data-testid="layoutsGridWrapper">
                <CardGrid
                  isLoading={isLayoutsLoading}
                  error={errorLayouts}
                  updatePage={getNextLayoutPage}>
                  {layoutsDetails.pages.map((page) =>
                    page.layouts.map((layout) => (
                      <CardGridItem item key={layout.id}>
                        <LayoutCard
                          key={layout.id}
                          layout={layout}
                          isSelected={layout.id === selectedLayout?.id}
                          onClicked={() => handleClick(layout)}
                        />
                      </CardGridItem>
                    )),
                  )}
                </CardGrid>
              </CardGridWrapper>
            </>
          )}
        </FluidWidthColumn>
      </Wrapper>
      {!isLayoutsLoading && (
        <ButtonContainer>
          <Button
            variant="outlined"
            onClick={handleBack}
            startIcon={<KeyboardArrowLeftIcon />}>
            Back
          </Button>
          <Box style={{ flexGrow: 1 }} />
          <CustomButton variant="text" onClick={handleCancel}>
            Cancel
          </CustomButton>
          <Button
            variant="contained"
            disabled={isEmpty(selectedLayout)}
            onClick={handleNext}>
            Next
          </Button>
        </ButtonContainer>
      )}

      {modal.open && (
        <Modal open={modal.open} onClose={modal.onCloseModal}>
          <AssemblyModal
            layout={selectedLayout as ILayout}
            handleClose={handleClose}
          />
        </Modal>
      )}
    </Container>
  );
};
